import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ButtonIconSet } from '../buttons.types';
import { MatMenu } from '@angular/material/menu';
import {
  MunilyIconButtonColor,
  MunilyIconButtonSize,
  MunilyIconButtonType,
  MunilyIconButtonVariant,
} from './icon-button.types';

@Component({
  selector: 'common-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconButtonComponent implements OnChanges {
  private readonly defaultIconSet: ButtonIconSet = 'material-symbols-rounded';

  @Input() icon = 'more_vert';
  @Input() ariaLabel = 'Icon button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() backgroundOpacity: number | null = null;

  @Input() iconSet: ButtonIconSet = 'material-symbols-rounded';
  @Input() color: MunilyIconButtonColor = 'primary';
  @Input() type: MunilyIconButtonType = 'filled';
  @Input() size: MunilyIconButtonSize = 'small';
  @Input() variant: MunilyIconButtonVariant = 'default';

  /** Material Symbols: `FILL` 1 (p. ej. `cancel` relleno). Solo aplica en `variant="plain"` o donde se use la clase en el icono. */
  @Input() iconFill = false;

  /** Evita que el click burbujee (p. ej. `mat-select`). */
  @Input() stopClickPropagation = false;

  /** Si se provee, el botón actúa como trigger de `mat-menu`. */
  @Input() menuTriggerFor: MatMenu | null = null;

  /**
   * Solo aplica a `variant="plain"`.
   * Padding simétrico en px alrededor del icono.
   * 0  → botón 24×24 (icono exacto, sin fondo).
   * >0 → botón (24 + padding×2)px, acepta fondo via `backgroundOpacity`.
   */
  @Input() iconPadding = 0;

  @Output() output = new EventEmitter<boolean>();

  /** Clases precomputadas (regla R21: un solo binding en HTML). */
  public computedPlainButtonClasses = '';
  public computedDefaultButtonClasses = '';
  public computedPlainIconClasses: string[] = [];
  public computedDefaultIconClass = '';

  get isPlainPadded(): boolean {
    return this.variant === 'plain' && this.iconPadding > 0;
  }

  ngOnChanges(_: SimpleChanges): void {
    this._recomputeClasses();
  }

  onClick(event?: Event): void {
    if (this.disabled || this.loading) return;
    if (event && this.stopClickPropagation) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.output.emit(true);
  }

  get backgroundOpacityClamped(): number | null {
    if (this.backgroundOpacity === null || this.backgroundOpacity === undefined) return null;
    if (Number.isNaN(this.backgroundOpacity)) return null;
    return Math.max(0, Math.min(1, this.backgroundOpacity));
  }

  private _recomputeClasses(): void {
    const iconSet = this.iconSet || this.defaultIconSet;
    this.computedDefaultIconClass = iconSet;
    this.computedPlainIconClasses = this.iconFill ? [iconSet, 'munily-icon-button__icon--fill'] : [iconSet];

    const colorClass = this._colorClass(this.color);
    const sizeClass = `munily-icon-button--size-${this.size}`;
    const typeClass = `munily-icon-button--type-${this.type}`;

    // Variante default (mat-icon-button)
    this.computedDefaultButtonClasses = ['munily-icon-button', typeClass, sizeClass, colorClass].join(' ');

    // Variante plain (solo icono)
    const plainBase = ['munily-icon-button', 'munily-icon-button--plain', colorClass];
    if (this.isPlainPadded) {
      plainBase.push('munily-icon-button--plain-padded');
    }
    this.computedPlainButtonClasses = plainBase.join(' ');
  }

  private _colorClass(color: MunilyIconButtonColor): string {
    switch (color) {
      case 'secondary':
        return 'munily-icon-button--color-secondary';
      case 'warning':
        return 'munily-icon-button--color-warning';
      case 'weak':
        return 'munily-icon-button--color-weak';
      case 'primary':
      default:
        return 'munily-icon-button--color-primary';
    }
  }
}
